import { useCallback, useEffect } from 'react';

import { useSession } from '@/providers/session';
import { useConfigStore } from '@/state/config';

const SAVE_BAR_ID = 'confetto-save-bar';

/**
 * The App Bridge contextual save bar.
 *
 * Rendered by Shopify OUTSIDE our iframe, at the top of the admin, which is why
 * it cannot be a Polaris component and why it is driven imperatively. BFS §4.2.5
 * requires it: a merchant with unsaved changes who clicks away must be warned by
 * the platform's own affordance, in the place they already look for it.
 *
 * ⚠ The `<ui-save-bar>` element must exist in the DOM before `saveBar.show()`
 * is called, and the discard/save handlers are wired through its BUTTONS, not
 * through the show() call. Calling show() on an id that is not mounted resolves
 * successfully and displays nothing — a silent no-op, which is the failure mode
 * to watch for here.
 */
export function SaveBar() {
  const { dirty, saving, save, discard } = useConfigStore();
  const session = useSession();

  const onSave = useCallback(() => {
    void save(session?.plan ?? 'free', session?.countryCode).then((ok) => {
      // Success is a toast; failure is the persistent Banner the store sets.
      if (ok) window.shopify?.toast?.show('Saved');
    });
  }, [save, session]);

  useEffect(() => {
    const bar = window.shopify?.saveBar;
    if (!bar) return;
    if (dirty) void bar.show(SAVE_BAR_ID);
    else void bar.hide(SAVE_BAR_ID);
  }, [dirty]);

  return (
    <ui-save-bar id={SAVE_BAR_ID}>
      {/* App Bridge reads `variant` off these buttons to decide which is the
          primary action; React does not know the attribute, hence the cast. */}
      <button
        {...({ variant: 'primary' } as Record<string, string>)}
        onClick={onSave}
        disabled={saving}
      />
      <button onClick={discard} disabled={saving} />
    </ui-save-bar>
  );
}
